import { formatterCurrency } from "../../formatters";
import { PAYMENT_TYPE } from "../../gtm_script";
import { doesNotHaveValue, isPJ, parseFetchResponse } from "../../helper_functions";
import { getDescriptiveTime } from "../../utils/get_descriptive_time";
import { addCorrId, tapAndLoadCorrId } from "../../utils/correlation_id_store";
import { useRecaptcha } from "../../utils/recaptcha_wrapper";
import { purchaseCallbackFor } from "../../utils/gtm_callback";

export function initPix({ showPaymentError, setLoading }) {
  const PIX_STATUS_POLLING_TIME_LIMIT = window.PM3.PIX_STATUS_POLLING_TIME_LIMIT || 1000 * 60 * 5; // 5 min in ms
  const PIX_STATUS_POLLING_INTERVAL = window.PM3.PIX_STATUS_POLLING_INTERVAL || 1000 * 5; // 5s in ms
  const PIX_EXPIRATION_UPDATE_INTERVAL = window.PM3.PIX_EXPIRATION_UPDATE_INTERVAL || 60 * 1000;

  async function fetchTransactionStatus(clientInstantPaymentId, signal) {
    return fetch(`/client_instant_payments/${clientInstantPaymentId}`, { signal, headers: addCorrId() })
      .then(tapAndLoadCorrId)
      .then(async (response) => {
        if (response.ok) {
          const { status, error } = await response.json();
          return { status, error };
        }
        return { status: undefined, error: await response.text() };
      })
      .catch((error) => {
        return { status: undefined, error: { type: "requestError", error } };
      });
  }

  let cancelPixPaymentValidationLoop;
  let timeLeftUpdateLoop;

  function closeQrCodeDialog() {
    cancelPixPaymentValidationLoop?.();
    timeLeftUpdateLoop?.();

    const dialog = document.getElementById("pix-dialog");

    if (dialog.open) {
      dialog.close();
    }

    const amountHeader = dialog.querySelector(".amount-header");
    const img = dialog.querySelector("img.qr-code-img");
    const copyCta = dialog.querySelector("button.qr-code-data-copy-cta");
    const expirationInfoSpan = dialog.querySelector(".qr-code-expiration-info");

    img.src = "";
    copyCta.dataset.qrCode = "";
    amountHeader.textContent = "";
    expirationInfoSpan.textContent = "Vence em";
  }

  document.getElementById("pix-dialog")
    .querySelector(".close-qr-code-cta")
    .addEventListener("click", closeQrCodeDialog);

  document.getElementById("pix-dialog").addEventListener("cancel", (event) => {
    event.preventDefault();
    closeQrCodeDialog();
  });

  let copiedMessageTimeoutRef;

  document.getElementById("pix-dialog")
    .querySelector("button.qr-code-data-copy-cta")
    .addEventListener("click", async function handleCopyCtaClick() {
      const dialog = document.getElementById("pix-dialog");
      const self = dialog.querySelector("button.qr-code-data-copy-cta");
      const qrCode = self.dataset.qrCode;
      if (!navigator || !("clipboard" in navigator)) {
        throw new Error("Failed to copy Pix QR Code, Clipboard API not available");
      }
      if (!qrCode) {
        throw new Error("Tried to copy empty Pix QR Code");
      }
      await navigator.clipboard.writeText(qrCode);
      if (copiedMessageTimeoutRef) {
        clearTimeout(copiedMessageTimeoutRef);
      }
      const label = self.querySelector(".qr-code-data-copy-cta-label");
      const previousText = label.textContent;
      label.textContent = "Copiado!";
      copiedMessageTimeoutRef = setTimeout(() => {
        if (label.textContent !== previousText) {
          label.textContent = previousText;
        }
      }, 3000);
    });

  function pollTransactionConfirmation(clientInstantPaymentId, expirationTime = null) {
    if (cancelPixPaymentValidationLoop) {
      cancelPixPaymentValidationLoop();
      cancelPixPaymentValidationLoop = undefined;
    }

    let resolveAsPending;

    const controller = new AbortController();

    let timeoutId;

    const promise = new Promise((resolve, reject) => {
      const expirationDelta = expirationTime ? expirationTime * 1000 : PIX_STATUS_POLLING_TIME_LIMIT;
      const timeoutLimit = Date.now() + expirationDelta;
      resolveAsPending = () => {
        resolve("pending");
      };
      function executeRequest() {
        if (timeoutId) clearTimeout(timeoutId);
        timeoutId = setTimeout(async () => {
          const expired = Date.now() > timeoutLimit;
          const { status, error } = await fetchTransactionStatus(clientInstantPaymentId, controller.signal);
          if (error) {
            promise.cancel(error);
            return reject(error);
          }
          if (status !== "pending") {
            return resolve(status);
          }
          if (expired) {
            return resolve("pending");
          }
          executeRequest();
        }, PIX_STATUS_POLLING_INTERVAL);
      }
      executeRequest();
    });
    promise.cancel = (failed) => {
      if (!failed && resolveAsPending) {
        resolveAsPending();
      }
      clearTimeout(timeoutId);
      controller.abort();
    };
    cancelPixPaymentValidationLoop = promise.cancel;
    return promise;
  }

  const expirationDateTimeFormatter = new Intl.DateTimeFormat(["pt-BR"], {
    day: "numeric",
    month: "numeric",
    hour: "numeric",
    minute: "2-digit",
    second: "2-digit"
  });

  function showQrCodeDialog(clientInstantPaymentId, amount, imgSrc, dataContents, expiresIn) {
    setLoading(true);
    showPaymentError("");
    const dialog = document.getElementById("pix-dialog");
    const amountHeader = dialog.querySelector(".amount-header");
    const img = dialog.querySelector("img.qr-code-img");
    const copyCta = dialog.querySelector("button.qr-code-data-copy-cta");
    const expirationInfoSpan = dialog.querySelector(".qr-code-expiration-info");

    img.src = imgSrc;
    copyCta.dataset.qrCode = dataContents;
    amountHeader.textContent = formatterCurrency.format(amount / 100);

    const expirationTimestamp = Date.now() + (expiresIn * 1000);

    function updateExpirationTime() {
      const timeLeftInMs = expirationTimestamp - Date.now();
      const expiration = expirationDateTimeFormatter.format(expirationTimestamp);
      if (timeLeftInMs < 0) {
        timeLeftUpdateLoop?.();
        expirationInfoSpan.textContent = `Venceu em ${expiration}`;
      } else {
        expirationInfoSpan.textContent = `Vence em ${expiration} (${getDescriptiveTime(timeLeftInMs / 1000, true)})`;
      }
    }

    if (PIX_EXPIRATION_UPDATE_INTERVAL) {
      const expirationUpdateInterval = setInterval(updateExpirationTime, PIX_EXPIRATION_UPDATE_INTERVAL);
      timeLeftUpdateLoop = () => {
        clearInterval(expirationUpdateInterval);
        timeLeftUpdateLoop = undefined;
      };
    }

    updateExpirationTime();

    dialog.showModal();

    setLoading(false);

    return pollTransactionConfirmation(clientInstantPaymentId, expiresIn * 1000);
  }

  return {
    showQrCodeDialog,
    closeQrCodeDialog
  };
}

export function setupPixPayment({
  clientForm,
  productForm,
  couponForm,
  bumpForm,
  formElements,
  formHelpers,
  showPaymentError,
  setLoading: baseSetLoading,
  gtm,
  setPaymentMethodSubmitHandler,
  onSuccess,
  onPending,
  onError,
  metadata: { mode = false } = {},
}) {
  const setLoading = (state) => baseSetLoading(state, {
    label: formElements.submitButtonLabel,
    loading: formElements.loadingIndicator
  });

  const { showQrCodeDialog, closeQrCodeDialog } = initPix({ showPaymentError, setLoading });

  const pixTotalAmountSpans = document.querySelectorAll("#pix_total_amount");

  let totalAmountOperators = [];

  function updateTotalAmount() {
    const { pixAmount } = formElements.getProductInfo({ formatted: false });
    const baseCents = (+pixAmount) * 100;
    const cents = totalAmountOperators.reduce((a, c, i, arr) => c(a, i, arr), baseCents);
    const amount = formatterCurrency.format(cents / 100);
    pixTotalAmountSpans.forEach(function updatePixTotalAmountSpans(span) {
      span.innerText = `${amount}`;
    });
  }

  if (couponForm && !couponForm.fixedCoupon) {
    let coupon
    function sumCouponInPixTotalAmount(value) {
      if (!coupon) return value;

      return coupon?.pixAmount || coupon?.pixAmount === 0 ? +coupon.pixAmount : value;
    }
    totalAmountOperators.unshift(sumCouponInPixTotalAmount);
    couponForm.addCouponChangeListener(function pixCouponChangeListener(state) {
      coupon = state;
      updateTotalAmount();
    });
  }

  if (bumpForm.enabled()) {
    let bumpState
    function sumBumpInPixTotalAmount(value) {
      if (!bumpState?.active) return value

      return value + bumpState.value
    }
    totalAmountOperators.push(sumBumpInPixTotalAmount);
    bumpForm.addChangeListener(function pixBumpChangeListener(state) {
      bumpState = state;
      updateTotalAmount();
    })
  }

  async function createOTOPixPayment(product_id, authenticity_token) {
    const gr_action = 'oto_pix_charge';
    const gr_token = await useRecaptcha();
    const body = {
      gr_action,
      gr_token,
      authenticity_token,
      provider: "malga",
      payment_method: "pix",
      product_id,
    };

    return fetch(
      "/client_payments/oto",
      {
        method: "POST",
        headers: addCorrId(new Headers({ "Content-Type": "application/json" })),
        body: JSON.stringify(body)
      }
    )
      .then(tapAndLoadCorrId)
      .then(parseFetchResponse)
      .catch((error) => {
        console.trace({ error });

        return null;
      });
  }

  async function createClientInstantPayment(product_id, authenticity_token, clientInfo) {
    const paymentInfo = formElements.getAmountAndCouponInformation();

    const gr_action = 'pix_charge';
    const grToken = await useRecaptcha(gr_action);
    const body = {
      gr_action,
      gr_token: grToken,
      authenticity_token,
      provider: "malga",
      payment_method: "pix",
      product_id,
      client_payment: {
        product_id,
        installments_count: 1,
        provider: "malga",
        payment_type: "pix"
      },
      order_bump_active: bumpForm.enabled() && bumpForm.active(),
      coupons: formElements.getAmountAndCouponInformation()
    };

    body.client_payment = {
      ...body.client_payment,
      product_id,
      name: clientInfo.name,
      email: clientInfo.email,
      is_pj: clientInfo.is_pj,
      is_foreigner: clientInfo.is_foreigner,
      document: clientInfo.document,
      phone_number: clientInfo.phone_number,
      zip_code: clientInfo.zip_code,
      whatsapp_accept: clientInfo.whatsapp_accept,
      coupon_id: paymentInfo.coupon_id,
      amount_off: paymentInfo.coupon_amount_off,
      percent_off: paymentInfo.counpon_percent_off,
      discount_amount: paymentInfo.discount_amount,
      amount: paymentInfo.subtotal_amount,
      total_amount: paymentInfo.pix_amount
    };

    /**
     * @type {{
     *  body: {
     *  id: string;
     *  provider: string;
     *  amount: number;
     *  stripe_customer: { id: string; };
     *  stripe_payment_intent: { id: string, client_secret: string; };
     *  }
     * }}
     */
    return fetch(
      "/client_instant_payments",
      {
        method: "POST",
        headers: addCorrId(new Headers({ "Content-Type": "application/json" })),
        body: JSON.stringify(body)
      }
    )
      .then(tapAndLoadCorrId)
      .then(parseFetchResponse)
      .catch((error) => {
        console.trace({ error });

        return null;
      });
  }

  // Submit the form to creates payment intent
  async function createPixCharge() {
    const authenticity_token = formElements.authenticityTokenElement.value;
    const product_id = productForm.getId();
    const clientInfo = clientForm.getFormData();

    const chargeStrategy = mode === 'oto' ? createOTOPixPayment : createClientInstantPayment;

    const clientInstantPaymentResponse = await chargeStrategy(product_id, authenticity_token, clientInfo);

    if (!clientInstantPaymentResponse) {
      setLoading(false);

      showPaymentError(
        "Ocorreu um erro. Verifique os dados preenchidos e tente novamente em alguns minutos"
      );

      return;
    }

    const {
      body: {
        id: clientInstantPaymentId,
        product_id: productId,
        product_name,
        // unit_amount: unitAmount, // exists here but not being used
        amount: totalAmount,
        // transaction_id, // exists here but not being used
        coupon_id, //  exists here but not being used
        pix,
        marketing_event_id: marketingEventIdentifier,
      }
    } = clientInstantPaymentResponse;

    if (!pix?.qrCodeData) {
      setLoading(false);

      showPaymentError(
        "Ocorreu um erro. Verifique os dados preenchidos e tente novamente em alguns minutos"
      );

      return;
    }

    const pixStatusUpdate = await showQrCodeDialog(clientInstantPaymentId, totalAmount, pix.qrCodeImageUrl, pix.qrCodeData, pix.expiresIn);

    if (pixStatusUpdate === "authorized") {
      const updateClientInstantPaymentResponse = await fetch(
        `/client_instant_payments/${clientInstantPaymentId}`,
        {
          method: "PATCH",
          headers: addCorrId(new Headers({ "Content-Type": "application/json" })),
          body: JSON.stringify({ authenticity_token })
        }
      )
        .then(tapAndLoadCorrId)
        .then(parseFetchResponse)
        .catch((error) => {
          console.error(
            "Failed to update client instant payment with confirmed payment intent:",
            error
          );
          return null;
        });
      if (
        updateClientInstantPaymentResponse === null ||
        updateClientInstantPaymentResponse.status !== 200
      ) {
        showPaymentError(
          "Ocorreu um erro ao confirmar o pagamento. Verifique os dados e tente novamente."
        );
        throw new Error(
          updateClientInstantPaymentResponse?.body ??
          "Failed to update client instant payment"
        );
      }
      setLoading(false);
      baseSetLoading(false);
      
      // Check if this is an OTO completion
      const responseBody = updateClientInstantPaymentResponse.body;
      if (typeof responseBody === 'string') {
        try {
          const parsedResponse = JSON.parse(responseBody);
          if (parsedResponse.status === 'oto_completed') {
            
            setTimeout(() => {
              window.parent.location.replace('https://pm3.com.br/');
            }, 2000);
            
            const paymentValues = formElements.getAmountAndCouponInformation({ formatted: false });
            await gtm.registerPurchaseEvent(PAYMENT_TYPE.pix, {
              coupon: coupon_id,
              discount: paymentValues.subtotal_amount - paymentValues.pix_amount,
              transactionId: clientInstantPaymentId,
              productId,
              productName: product_name,
              price: Number(totalAmount) / 100,
              fullName: clientInfo.name,
              email: clientInfo.email,
              phone: clientInfo.phone_number
            });
            
            return;
          }
        } catch (e) {
          
        }
      }
      
      const paymentValues = formElements.getAmountAndCouponInformation({ formatted: false });
      await gtm.registerPurchaseEvent(
        PAYMENT_TYPE.pix,
        {
          coupon: coupon_id,
          discount: paymentValues.subtotal_amount - paymentValues.pix_amount,
          transactionId: clientInstantPaymentId,
          productId,
          productName: product_name,
          price: Number(totalAmount) / 100,
          fullName: clientInfo.name,
          email: clientInfo.email,
          phone: clientInfo.phone_number
        },
        purchaseCallbackFor(marketingEventIdentifier),
      );
      onSuccess();
    } else if (["pre_authorized", "pending"].includes(pixStatusUpdate)) {
      setLoading(false);
      baseSetLoading(false);
      onPending();
    } else {
      baseSetLoading(false);
      setLoading(false);
      closeQrCodeDialog();
      onError();
    }
  };

  async function handlePixChargeSubmission(_event) {
    const clientInfo = clientForm.getFormData();
    showPaymentError("");

    if (mode !== "oto") { // if OTO do not validate client form
      if (!formHelpers.cpfCnpjValidator()) {
        showPaymentError(`${isPJ(clientForm.inputs.clientType.value) ? "CNPJ" : "CPF"} inválido`);
        return;
      }

      if (
        clientForm.isClientPj() &&
        doesNotHaveValue(clientForm.inputs.stateRegistrationInput) &&
        doesNotHaveValue(clientForm.inputs.municipalRegistrationInput)
      ) {
        if (formElements.stateRegistrationAlertMessageElement) {
          formElements.stateRegistrationAlertMessageElement.className = "";
          formElements.stateRegistrationAlertMessageElement.style =
            "color:#FF5757; font-weight:600;";
        }
        showPaymentError("Verifique as informações inseridas");
        return;
      }

      if (formElements.stateRegistrationAlertMessageElement) {
        formElements.stateRegistrationAlertMessageElement.className = "hidden";
      }

    }
    baseSetLoading(true);
    setLoading(true);

    const productInfo = formElements.getProductInfo({ formatted: false });
    const paymentValues = formElements.getAmountAndCouponInformation({ formatted: false });

    gtm.registerCheckoutOptionEvent(PAYMENT_TYPE.pix, {
      price: paymentValues.pix_amount,
      discount: paymentValues.subtotal_amount - paymentValues.pix_amount,
      coupon: paymentValues.coupon_id,
      productName: productInfo.productName,
      productId: productInfo.productId,
      fullName: clientInfo.name,
      email: clientInfo.email,
      phone: clientInfo.phone_number
    });

    createPixCharge()
      .catch((error) => {
        showPaymentError(error.message);
        baseSetLoading(false);
        setLoading(false);
        console.trace(
          "Failed to create payment method",
          error.message,
          error?.stack ?? error
        );
      });
  };

  setPaymentMethodSubmitHandler("pix", handlePixChargeSubmission);
}
